import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, CheckCircle2, Loader2, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  checkStripePrices,
  type StripeCheckResult,
} from "@/lib/stripe-check.functions";
import { seoHead } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin_/stripe-check")({
  head: () =>
    seoHead({ title: "Stripe Price 検証 - Admin", path: "/admin/stripe-check" }),
  component: StripeCheckPage,
});

function StripeCheckPage() {
  const fn = useServerFn(checkStripePrices);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<StripeCheckResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setErr(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const headers = sessionData.session
        ? { Authorization: `Bearer ${sessionData.session.access_token}` }
        : undefined;
      setResult(await fn({ headers }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const allLive = result
    ? result.secretKeyLive === true && result.rows.every((r) => r.ok && r.livemode)
    : false;

  return (
    <main className="min-h-screen bg-background px-4 py-8">
      <div className="mx-auto max-w-4xl">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/admin">
            <ArrowLeft className="mr-1 h-4 w-4" />
            管理ダッシュボードへ
          </Link>
        </Button>

        <Card className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold">Stripe Price ID 検証</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Cloud Secrets の各 Price ID を Stripe API に問い合わせ、本番モードか / 商品名 / 金額を表示します。
              </p>
            </div>
            <Button onClick={run} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              検証実行
            </Button>
          </div>

          {err && (
            <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {err}
            </div>
          )}

          {result && (
            <div className="mt-6 space-y-4">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Badge variant={result.secretKeyLive ? "default" : "destructive"}>
                  Secret Key: {result.secretKeyPrefix ?? "未設定"}
                  {result.secretKeyLive === true && " (LIVE)"}
                  {result.secretKeyLive === false && " (TEST)"}
                </Badge>
                <Badge variant={result.webhookConfigured ? "default" : "destructive"}>
                  Webhook Secret: {result.webhookConfigured ? "設定済" : "未設定"}
                </Badge>
                {allLive && (
                  <Badge variant="default" className="bg-emerald-600">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    全て本番値
                  </Badge>
                )}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Secret 名</TableHead>
                    <TableHead>Price ID</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>商品 / 金額</TableHead>
                    <TableHead>状態</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.rows.map((r) => (
                    <TableRow key={r.key}>
                      <TableCell className="font-mono text-xs">{r.key}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.priceId ?? "—"}
                      </TableCell>
                      <TableCell>
                        {r.ok ? (
                          <Badge
                            variant={r.livemode ? "default" : "destructive"}
                            className={r.livemode ? "bg-emerald-600" : ""}
                          >
                            {r.livemode ? "LIVE" : "TEST"}
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.ok ? (
                          <>
                            <div>{r.productName}</div>
                            <div className="text-xs text-muted-foreground">
                              {r.unitAmount != null
                                ? `${r.unitAmount.toLocaleString()} ${r.currency?.toUpperCase()}`
                                : "—"}
                              {r.recurring ? ` / ${r.recurring}` : ""}
                              {r.active === false ? " (inactive)" : ""}
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-destructive">{r.error}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.ok && r.livemode && r.active !== false ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-destructive" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>
    </main>
  );
}
